import Link from 'next/link';
import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { ArrowBack } from '@/ui/common/Icons/ArrowBack';
import { MenuButton } from '@/ui/common/Buttons/MenuButton';
import useOnClickOutside from '@/service/hooks/useClickOutside';
import { ETripView } from '@/domain/Activities.domain';
import { ToggleTheme } from './ToggleTheme';
import { useAttractionsStore } from '@/service/state/Attractions/useAttractionsStore';
import { usePathname } from 'next/navigation';
import { WaverezLogo } from '@/ui/common/Icons/WaverezLogo';
import { useCartStore } from '@/service/state/Cart/useCartStore';
import { getCommonPhoneFormatURI } from '@/service/utils/getCommonPhoneFormat';
import { usePartnerStore } from '@/service/state/Partner/usePartnerStore';

type TSideMenuProps = {
    partnerSlug: string;
    partnerWebsite: string;
    phoneNumber: string;
    partnerLogoSrc: string;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
};

export const SideMenu = ({
    partnerWebsite,
    partnerLogoSrc,
    partnerSlug,
    phoneNumber,
    setIsOpen,
}: TSideMenuProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const [touchStartX, setTouchStartX] = useState(0);
    const [touchStartY, setTouchStartY] = useState(0);
    const pathname = usePathname();

    const handleTripViewChange = useAttractionsStore((state) => state.handleTripViewChange);
    const cartItems = useCartStore((state) => state.cart?.cart_items)?.length;
    const showThenHideCartToast = useCartStore(
        ({ showThenHideCartToast }) => showThenHideCartToast
    );
    const callToBookText = usePartnerStore((state) => state.texts?.['Call to Book']);

    const hideMenu = () => {
        if (ref.current) {
            ref.current.style.transform = 'translateX(-100%)';
        }
    };

    const handleClickOutside = () => {
        hideMenu();

        setTimeout(() => {
            setIsOpen(false);
        }, 300);
    };

    const handleCalendarLink = (view: ETripView) => {
        handleTripViewChange(view);
        handleClickOutside();
    };

    const stoped = (e: React.TouchEvent<HTMLDivElement>) => {
        const diffX = touchStartX - e.changedTouches[0].clientX;
        const diffY = touchStartY - e.changedTouches[0].clientY;
        if (diffX > diffY && diffX > 40) {
            setTouchStartX(0);
            setTouchStartY(0);
            handleClickOutside();
        }
    };

    useOnClickOutside(ref, handleClickOutside);
    const isBillPage = pathname.includes('/bill/');

    return (
        <div
            className={`relative mr-8 h-full w-[95vw] max-w-[344px] animate-rollup rounded-r-2xl bg-white p-[14px_40px_78px] shadow-lightPopup transition-all duration-300 ease-out dark:bg-blue-main dark:shadow-darkCard`}
            ref={ref}
            onTouchStart={(e) => {
                setTouchStartX(e.changedTouches[0].clientX);
                setTouchStartY(e.changedTouches[0].clientY);
            }}
            onTouchEnd={stoped}
        >
            <div className="flex items-center justify-between border-b border-gray-4 pb-3 dark:border-blue-1">
                <img
                    src={partnerLogoSrc}
                    className="shadow-main  h-10 overflow-hidden rounded"
                    alt={partnerSlug}
                    data-test-id="partner-logo"
                    onError={({ target }) => {
                        const e = target as HTMLImageElement;
                        e.src = '/img/PoweredBy.png';
                    }}
                />
                <div onClick={handleClickOutside}>
                    <ArrowBack
                        className="cursor-pointer [&>*]:fill-dark-4 [&>*]:hover:fill-brand [&>*]:dark:fill-white"
                        data-test-id="back-button"
                    />
                </div>
            </div>
            <div className=" pb-6 pt-7">
                <Link
                    href={partnerWebsite}
                    className=" tx-large-medium mb-6 px-5 text-gray-1 hover:underline dark:text-white"
                    data-test-id="back-to-website"
                    prefetch={false}
                >
                    Back to Website
                </Link>
                <Link
                    href={`/${partnerSlug}/`}
                    prefetch={false}
                    data-test-id="browse-by-activity-link"
                >
                    <MenuButton
                        icon="Browse"
                        label="Browse by activity"
                        onClick={() => handleCalendarLink(ETripView.BROWSE_BY_ACTIVITY)}
                    />
                </Link>
                <Link href={`/${partnerSlug}/`} prefetch={false} data-test-id="calendar-view-link">
                    <MenuButton
                        icon="Calendar"
                        label="Calendar view"
                        onClick={() => handleCalendarLink(ETripView.CALENDAR)}
                    />
                </Link>
                {!isBillPage && (
                    <Link
                        href={`/${partnerSlug}/cart`}
                        onClick={(e) => {
                            if (!cartItems) {
                                e.preventDefault();
                                showThenHideCartToast();
                            }
                            handleClickOutside();
                        }}
                        data-test-id="cart-link"
                        prefetch={false}
                    >
                        <div className="relative">
                            <MenuButton icon="Cart" label="Cart" />
                            {!!cartItems && (
                                <span className="icon-status-badge left-6 right-auto">
                                    {cartItems}
                                </span>
                            )}
                        </div>
                    </Link>
                )}
            </div>
            {!pathname.includes('/checkout') && !isBillPage && (
                <div
                    className="border-y border-gray-4 dark:border-blue-1"
                    data-test-id="toggle-theme"
                >
                    <ToggleTheme />
                </div>
            )}
            <Link
                href={getCommonPhoneFormatURI(phoneNumber)}
                target="_blank"
                prefetch={false}
                className="mt-7"
            >
                <MenuButton
                    icon="Phone"
                    label={callToBookText || 'Call to book'}
                    data-test-id="phone-link"
                />
            </Link>
            <div className="tx-large-medium absolute bottom-9 left-0 flex w-full flex-col items-center text-gray-3">
                <Link
                    href={`/${partnerSlug}/terms/`}
                    className="tx-large-medium mb-4 text-brand underline"
                    data-test-id="terms-link"
                    onClick={handleClickOutside}
                    prefetch={false}
                >
                    Policies & Terms
                </Link>
                Powered By
                <Link href="https://www.waverez.com" prefetch={false} data-test-id="waverez-link">
                    <WaverezLogo className="h-6 [&>*:nth-child(1)]:fill-gray-3 [&>*:nth-child(1)]:dark:fill-ash [&>*:nth-child(10)]:fill-gray-3 [&>*:nth-child(10)]:dark:fill-ash [&>*:nth-child(2)]:fill-gray-3 [&>*:nth-child(2)]:dark:fill-ash [&>*:nth-child(7)]:fill-gray-3 [&>*:nth-child(7)]:dark:fill-ash [&>*:nth-child(8)]:fill-gray-3 [&>*:nth-child(8)]:dark:fill-ash [&>*:nth-child(9)]:fill-gray-3 [&>*:nth-child(9)]:dark:fill-ash" />
                </Link>
            </div>
            <div className="absolute right-3 top-[40vh] h-[20vh] w-[6px] rounded-lg bg-gray-4 dark:bg-blue-1" />
        </div>
    );
};
